import {
  mdiAccountOff,
  mdiAlertCircleOutline,
  mdiCalendarRemove,
  mdiCheckDecagram,
  mdiLinkOff,
} from '@quasar/extras/mdi-v7';
import type { JoinClassError } from 'src/modules/School/composables/useSchoolData';
import type { MessageSchema } from 'boot/i18n';

/**
 * Presentation model for the invite-link join error page. Maps a machine-readable
 * backend failure to a human-readable icon + i18n keys + the actions worth offering.
 * Pure: the view resolves the keys with `t()`; the component only renders.
 */
export interface JoinErrorPresentation {
  icon: string;
  // Typed against the locale schema so a renamed/removed key fails the build
  // rather than rendering the raw key to a student.
  titleKey: keyof MessageSchema;
  messageKey: keyof MessageSchema;
  /** Offer "Try again" — the failure looks transient (network / server), so a retry may work. */
  showRetry: boolean;
}

export function joinErrorPresentation(
  error: JoinClassError | null,
): JoinErrorPresentation {
  // No structured error (e.g. a malformed link with no class id).
  if (!error) {
    return {
      icon: mdiLinkOff,
      titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
      messageKey: 'SCHOOL_JOIN_ERROR_INVALID_LINK',
      showRetry: false,
    };
  }

  // A prior enrollment exists but is terminal — the invite link can't reopen it.
  if (error.code === 'ENROLLMENT_NOT_REOPENABLE') {
    if (error.enrollmentStatus === 'declined') {
      return {
        icon: mdiAccountOff,
        titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
        messageKey: 'SCHOOL_JOIN_ERROR_DECLINED',
        showRetry: false,
      };
    }
    if (error.enrollmentStatus === 'dropped') {
      return {
        icon: mdiAccountOff,
        titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
        messageKey: 'SCHOOL_JOIN_ERROR_DROPPED',
        showRetry: false,
      };
    }
    if (error.enrollmentStatus === 'completed') {
      return {
        icon: mdiCheckDecagram,
        titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
        messageKey: 'SCHOOL_JOIN_ERROR_COMPLETED',
        showRetry: false,
      };
    }
    return {
      icon: mdiAlertCircleOutline,
      titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
      messageKey: 'SCHOOL_JOIN_ERROR_CLOSED',
      showRetry: false,
    };
  }

  // The target semester has ended (or the invite pointed at an ended one).
  if (error.code === 'SEMESTER_ENDED') {
    return {
      icon: mdiCalendarRemove,
      titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
      messageKey: 'SCHOOL_JOIN_ERROR_SEMESTER_ENDED',
      showRetry: false,
    };
  }

  // The link references a semester/term that no longer resolves.
  if (
    error.code === 'SEMESTER_NOT_FOUND' ||
    error.name === 'NoCurrentSemesterError'
  ) {
    return {
      icon: mdiLinkOff,
      titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
      messageKey: 'SCHOOL_JOIN_ERROR_INVALID_LINK',
      showRetry: false,
    };
  }

  // The class itself is gone / inactive.
  if (error.status === 404) {
    return {
      icon: mdiLinkOff,
      titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
      messageKey: 'SCHOOL_JOIN_ERROR_CLASS_NOT_FOUND',
      showRetry: false,
    };
  }

  // Anything else (network hiccup, 5xx) — likely transient, so offer a retry.
  return {
    icon: mdiAlertCircleOutline,
    titleKey: 'SCHOOL_JOIN_ERROR_TITLE',
    messageKey: 'SCHOOL_JOIN_ERROR_GENERIC',
    showRetry: true,
  };
}
